import type {Schemas} from 'domainTypes/dto';
import {ebsApi} from 'store/ebsApi';
import type {NoteData} from './types';
import {mapCommentDto, mapToPostPutCommentDto} from './mappers';

export const notesApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getNotes: build.query<NoteData[], string, Schemas.CommentDto[]>({
      query: contextId => `comment?contextId=${contextId}`,
      keepUnusedDataFor: 60,
      transformResponse: (dtos: Schemas.CommentDto[]) =>
        dtos.map(mapCommentDto),
      providesTags: (_result, _error, contextId) => [
        {type: 'Notes', id: contextId},
      ],
    }),
    getNotesByContextIds: build.query<
      NoteData[],
      string[],
      Schemas.CommentDto[]
    >({
      query: contextIds => ({
        url: 'comment/contextIds',
        method: 'POST',
        body: contextIds,
      }),
      transformResponse: (dtos: Schemas.CommentDto[]) =>
        dtos.map(mapCommentDto),
      onQueryStarted: async (contextIds, {dispatch, queryFulfilled}) => {
        const {data: notes} = await queryFulfilled;
        // Tagging getNotes entry per context so useGetNotesQuery(contextId) won't refetch.
        dispatch(
          notesApi.util.upsertQueryEntries(
            contextIds.map(contextId => ({
              endpointName: 'getNotes' as const,
              arg: contextId,
              value: notes.filter(n => n.contextId === contextId),
            })),
          ),
        );
      },
    }),
    postNote: build.mutation<NoteData, NoteData, Schemas.CommentDto>({
      query: note => ({
        url: `comment`,
        method: 'POST',
        body: mapToPostPutCommentDto('POST', note),
      }),
      transformResponse: mapCommentDto,
      invalidatesTags: (_result, _error, note) => [
        {type: 'Notes', id: note.contextId},
      ],
    }),
    putNote: build.mutation<NoteData, NoteData, Schemas.CommentDto>({
      query: note => ({
        url: `comment/${note.id}`,
        method: 'PUT',
        body: mapToPostPutCommentDto('PUT', note),
      }),
      transformResponse: mapCommentDto,
      invalidatesTags: (_result, _error, note) => [
        {type: 'Notes', id: note.contextId},
      ],
    }),
    deleteNote: build.mutation<void, {id: string; contextId: string}>({
      query: ({id}) => ({
        url: `comment/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {contextId}) => [
        {type: 'Notes', id: contextId},
      ],
    }),
  }),
});

export const {
  useGetNotesQuery,
  useGetNotesByContextIdsQuery,
  usePostNoteMutation,
  usePutNoteMutation,
  useDeleteNoteMutation,
} = notesApi;
